import { dropTask, restartableTask } from 'ember-concurrency';
import _groupBy from 'lodash/groupBy';
import _sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import { ChartColorScale, ChartColorScaleVars } from 'volta/components/bv-chart/theme';
// @ts-ignore
import FormatCell from 'volta/components/cell/format-cell';
import ForecastQtyCell, { TSelectedRow } from 'volta/components/sku-details/forecasts/qty-cell';
import DemandForecast, {
  DemandForecastQueries,
  DemandForecastResourceName
} from 'volta/models/demand-forecast';
import { FrequencyDict } from 'volta/utils/chart-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { getColor } from 'volta/utils/style-utils';

import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { ITransactionAgg } from 'volta/models/types/transaction';
import type { TAduFrequency } from 'volta/models/types/planning-settings';
import type BvMetricsService from 'volta/services/bv-metrics';
import type { ICmdResult } from 'volta/utils/api/jsonapi-types';
import type IntlService from 'ember-intl/services/intl';
import type LabelService from 'volta/services/label-service';
import type { IBvTableColumn, IBvTableSort } from 'volta/components/bv-table';
import type { IDemandForecastPeriod, IDemandForecastAgg } from 'volta/models/types/forecasts';
import type StoreService from 'volta/services/store';
import type AuthorizationService from 'volta/services/authorization';
import type BvFlashService from 'volta/services/bv-flash';
import type { TSkuDetailModalTab } from 'volta/services/sku-modal-service';

interface IArgs {
  skuId: string;
  unitMeasure: string;
  disabled?: boolean;
  isLoading?: boolean;
  switchTab?: (tab: TSkuDetailModalTab) => void;
}

export default class SkuDetailsForecasts extends Component<IArgs> {
  /*
    Services
  */

  @service store!: StoreService;
  @service intl!: IntlService;
  @service labelService!: LabelService;
  @service bvFlash!: BvFlashService;
  @service authorization!: AuthorizationService;
  @service bvMetrics!: BvMetricsService;

  /*
    Tracked Properties
  */

  @tracked forecastData?: IDemandForecastAgg[];
  @tracked forecastTypesFilter?: string[];
  @tracked frequency: TAduFrequency = 'WEEKLY';
  @tracked tableSorts: IBvTableSort[] = [{ valuePath: 'date', isAscending: true }];
  @tracked transactions: ITransactionAgg[] = [];
  @tracked isTableCollapsed = false;

  /*
    Properties
  */

  resourceName = DemandForecastResourceName;

  /*
    Getters
  */

  @cached
  get tableColumns(): IBvTableColumn[] {
    const { forecastTypesFilter, forecastTypes, seriesColors } = this;
    const startOfMonth = moment().startOf('month');
    const period = FrequencyDict[this.frequency].period;
    const cols: IBvTableColumn[] = [
      {
        columnName: this.intl.t('inventoryDate'),
        valuePath: 'date',
        isResizable: true,
        isReorderable: false,
        minWidth: 50,
        width: 80,
        isFixed: 'left',
        format: (f: IDemandForecastPeriod) => f.dateStr
      },
      {
        columnName: this.intl.t('outgoing'),
        valuePath: 'outgoing',
        isResizable: true,
        isReorderable: false,
        minWidth: 50,
        width: 80,
        isFixed: 'left',
        textAlign: 'right',
        classNames: 'u-text-mono',
        headerIconRight: {
          glyph: 'ellipse',
          size: 'xs',
          classNames: `u-color-blue`
        },
        cellComponent: FormatCell,
        format: (model: IDemandForecastPeriod) => {
          return {
            value: model.outgoing,
            dataType: 'number',
            unit: isPresent(model.outgoing)
              ? this.labelService.unitMeasure(this.args.unitMeasure)
              : undefined
          };
        }
      }
    ];
    return cols.concat(
      (forecastTypesFilter ?? forecastTypes).map((ft: string) => {
        return {
          columnName: ft,
          valuePath: ft,
          sortKey: `${ft}.demandSum`,
          headerIconRight: {
            glyph: 'ellipse',
            size: 'xs',
            classNames: `${seriesColors[ft].css}`
          },
          classNames: 'u-text-mono',
          cellComponent: ForecastQtyCell,
          textAlign: 'right',
          isResizable: true,
          isReorderable: true,
          minWidth: 50,
          width: 80,
          format: (forecast: IDemandForecastAgg) => {
            const forecastStartOfMonth = moment.utc(forecast.date).endOf(period).startOf('month');
            const isBeforeCurrentMonth = forecastStartOfMonth.isBefore(startOfMonth, 'd');
            return {
              unitMeasure: this.args.unitMeasure,
              disabled:
                this.args.disabled ||
                !this.authorization.can('demandForecasts', 'UpdateQtyForPeriod') ||
                isBeforeCurrentMonth,
              isLoading: this.args.isLoading,
              forecastType: ft,
              handleChangeDemandQty: this.handleChangeDemandQty
            };
          }
        };
      })
    );
  }

  get tableSortProperties() {
    return this.tableSorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  @sort('tableData', 'tableSortProperties')
  sortedTableData!: IDemandForecastPeriod[];

  get forecastTypes() {
    return [...new Set(this.forecastData?.map((f) => f.forecastType))].sort();
  }

  @cached
  get seriesColors(): Record<string, { css: string; rgb: string }> {
    let colorCount = 0;
    const series: Record<string, { css: string; rgb: string }> = {};
    this.forecastTypes.forEach((type) => {
      series[type] = {
        css: `u-color-${ChartColorScaleVars[colorCount]}`,
        rgb: ChartColorScale[colorCount]!
      };
      colorCount++;

      if (colorCount % ChartColorScale.length === 0) {
        colorCount = 0;
      }
    });
    return {
      ...series,
      [this.intl.t('outgoing')]: { rgb: getColor('blue-4')!, css: 'u-color-blue-4' }
    };
  }

  get tableData() {
    const { forecastData = [], frequency, isTableCollapsed } = this;

    const months = _groupBy(
      _sortBy(forecastData, (d) => d.date),
      (d) => d.month
    );

    return Object.entries(months).map(([mDate, mItems]) => {
      const dateStr = moment.utc(mDate).format('YYYY MMMM');
      const month: IDemandForecastPeriod = {
        id: dateStr,
        date: mDate,
        dateStr: dateStr,
        outgoing: moment.utc(mDate).isBefore(moment.utc())
          ? mItems[0]?.monthlyOutgoingSum
          : undefined,
        frequency: 'MONTHLY',
        isCollapsed: isTableCollapsed,
        series: {}
      };
      const mTypes = _groupBy(mItems, (i) => i.forecastType);

      Object.entries(mTypes).forEach(([ft, ftItems]) => {
        month.series[ft] = {
          demandSum: ftItems[0]?.monthlyDemandSum ?? 0,
          demandAvg: ftItems[0]?.monthlyDemandAvg ?? 0
        };
      });

      if (frequency !== 'MONTHLY') {
        const weeks = _groupBy(mItems, (d) => d.week);
        month.children = Object.entries(weeks).map(([wDate, wItems]) => {
          const dMoment = moment.utc(wDate);
          const week: IDemandForecastPeriod = {
            id: dMoment.format('GGGG Wo'),
            date: wDate,
            dateStr: `${this.intl.t('week')} ${dMoment.format('W')}`,
            outgoing: moment.utc(wDate).isBefore(moment.utc())
              ? wItems[0]?.weeklyOutgoingSum
              : undefined,
            frequency: 'WEEKLY',
            isCollapsed: isTableCollapsed,
            series: {}
          };

          const wTypes = _groupBy(wItems, (i) => i.forecastType);

          Object.entries(wTypes).forEach(([ft, ftItems]) => {
            week.series[ft] = {
              demandSum: ftItems[0]?.weeklyDemandSum ?? 0,
              demandAvg: ftItems[0]?.weeklyDemandAvg ?? 0
            };
          });

          if (frequency !== 'WEEKLY') {
            const days = _groupBy(wItems, (d) => d.date);
            week.children = Object.entries(days).map(([date, dItems]) => {
              const d = moment.utc(date);
              const day: IDemandForecastPeriod = {
                id: date,
                date: date,
                dateStr: d.format('L'),
                outgoing: dItems[0]?.outgoing,
                frequency: 'DAILY',
                isCollapsed: isTableCollapsed,
                series: {}
              };

              const dTypes = _groupBy(dItems, (i) => i.forecastType);

              Object.entries(dTypes).forEach(([ft, ftItems]) => {
                day.series[ft] = {
                  demandSum: ftItems[0]?.dailyDemand ?? 0,
                  demandAvg: ftItems[0]?.dailyDemand ?? 0
                };
              });

              return day;
            });
          }
          return week;
        });
      }

      return month;
    });
  }

  /*
    Actions
  */

  selectFrequency = (frequency: TAduFrequency) => {
    if (this.frequency !== frequency) {
      this.frequency = frequency;
      this.isTableCollapsed = false;
      this.bvMetrics.trackAction(this, 'selectFrequency', frequency);
    }
  };

  handleToggleCollapseAll = () => {
    this.isTableCollapsed = !this.isTableCollapsed;
    this.bvMetrics.trackAction(this, 'handleCollapseExpandTable', `${this.isTableCollapsed}`);
  };

  handleToggleCollapseRow = (_evt: PointerEvent, row: { isCollapsed?: boolean }) => {
    row.isCollapsed = !Boolean(row.isCollapsed);
  };

  handleLegendSelect = (forecastTypes: string[]) => {
    this.forecastTypesFilter = forecastTypes;
  }

  /*
    Tasks
   */

  handleChangeDemandQty = dropTask(
    async (selectedRow: TSelectedRow, qtyFormat: 'percent' | 'number') => {
      try {
        const result = (await DemandForecast.updateQtyForPeriod({
          skuId: this.args.skuId,
          [qtyFormat === 'percent' ? 'percent' : 'quantity']: +selectedRow.value,
          frequency: selectedRow.row.frequency,
          startDate: selectedRow.row.date,
          forecastType: selectedRow.forecastType
        })) as ICmdResult<{ updatedIds: string[] }>;
        this.bvFlash.success(
          this.intl.t('bvList.bulkActions.updateSuccess', {
            count: result.data.updatedIds.length,
            resourceNameSingular: this.intl.t(this.resourceName.singular),
            resourceNamePlural: this.intl.t(this.resourceName.plural)
          })
        );
        this.bvMetrics.trackAction(this, 'handleChangeDemandQty');
        this.fetchForecastTask.perform();
      } catch (error) {
        defaultErrorHandler(error);
        this.bvFlash.error(
          this.intl.t('bvList.bulkActions.updateFail', {
            resourceNamePlural: this.intl.t(this.resourceName.plural)
          })
        );
      }
    }
  );

  fetchForecastTask = restartableTask(async () => {
    try {
      const { skuId } = this.args;

      this.forecastData = await this.store.customQuery(
        DemandForecastResourceName.singular,
        skuId,
        DemandForecastQueries.tsBySku
      );
      this.forecastTypesFilter = this.forecastTypes;
      return this.forecastData;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });
}
